import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Briefcase, Eye, Trash, ExternalLink, Recycle, DeleteIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePagination } from '../hooks/usePagination';
import { toast } from 'react-toastify';
import { Pagination } from '../utils/Pagination';
import debounce from 'lodash/debounce';
import { formatDate } from '../utils/Config';
import { deleteJob, fetchDeletedJobs, fetchJobs, forceDeleteJob, restoreJob } from '../redux/JobsSlice';
import { Oval } from 'react-loader-spinner';
import { fetchJobCategories } from '../redux/JobCategoriesSlice';
import { FaTrashAlt } from 'react-icons/fa';

const JOB_TYPES = ['All Types', 'Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship'];
const JOB_CATEGORIES = ['All Categories', 'Engineering', 'Design', 'Marketing', 'Sales', 'Customer Service', 'Management'];
const LOCATIONS = ['All Locations types', 'Remote', 'On-site', 'Hybrid'];

const JobSearch = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const { loading } = useSelector((state) => state.jobs);

  const debouncedSearch = useCallback(
    debounce((term) => {
      dispatch(fetchJobs({
        page: 1,
        limit: 15,
        search: term
      }));
    }, 500),
    [dispatch]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search
          className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${loading ? 'text-blue-500 animate-pulse' : 'text-gray-400'}`}
          size={20}
        />
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search jobs..."
          className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>
  );
};

const JobFilters = ({ filters, onFilterChange, categories }) => {
  // Prepare categories for the dropdown
  const categoryOptions = ['All Categories', ...(categories?.map(cat => cat.name) || [])];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      <select
        value={filters.type || 'All Types'}
        onChange={(e) => onFilterChange('type', e.target.value)}
        className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        {JOB_TYPES.map(type => (
          <option key={type} value={type}>{type}</option>
        ))}
      </select>

      <select
        value={filters.category || 'All Categories'}
        onChange={(e) => onFilterChange('category', e.target.value)}
        className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        {categoryOptions.map(category => (
          <option key={category} value={category}>{category}</option>
        ))}
      </select>

      <select
        value={filters.location || 'All Locations types'}
        onChange={(e) => onFilterChange('location', e.target.value)}
        className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        {LOCATIONS.map(location => (
          <option key={location} value={location}>{location}</option>
        ))}
      </select>
    </div>
  );
};

const JobTable = ({ jobs }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleView = (id) => {
    navigate(`/dashboard/jobs/${id}`);
  };

  const handleDelete = async (id) => {
    try {
      setIsDeleting(true);
      await dispatch(deleteJob(id)).unwrap();
      toast.success('Job deleted successfully');
      setShowDeleteModal(false);
    } catch (error) {
      toast.error(error.message || 'Failed to delete job');
    } finally {
      setIsDeleting(false);
    }
  };


  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white rounded-lg overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Job Details
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Category
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Location
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Salary Range
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Deadline
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {jobs?.map((job) => (
            <tr key={job.id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    {job?.company_logo ? (
                      <img
                        src={job.company_logo}
                        alt=""
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <Briefcase className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {job?.title}
                    </div>
                    <div className="text-sm text-gray-500">
                      {job?.company_name}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">{job?.job_category?.name}</div>
                <div className="text-sm text-gray-500">{job?.job_type}</div>
              </td>
              <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">{job?.location}</div>
                  <div className="text-sm text-gray-500">{job?.location_type}</div>
                
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  {`${job?.salary_currency} ${job?.salary_min?.toLocaleString() || ''} - ${job?.salary_max?.toLocaleString() || ''}`}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  {formatDate(job?.application_deadline)}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => handleView(job.id)}
                    className="text-blue-600 hover:text-blue-900"
                    title="View Details"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                  {job.application_link && (
                    <a
                      href={job.application_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-900"
                      title="Apply External"
                    >
                      <ExternalLink className="h-5 w-5" />
                    </a>
                  )}
                  <button
                    onClick={() => {
                      setSelectedJobId(job.id);
                      setShowDeleteModal(true);
                    }}
                    className="text-red-600 hover:text-red-900"
                    title="Delete Job"
                  >
                    <Trash className="h-5 w-5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full mx-6 lg:max-w-md">
            <h2 className="text-xl font-bold mb-4">Delete Job</h2>
            <p>Are you sure you want to delete this job posting? This action cannot be undone.</p>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(selectedJobId)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                {isDeleting ? (
                  <div className="flex justify-center items-center">
                    <Oval
                      visible={true}
                      height="20"
                      width="20"
                      color="#ffffff"
                      ariaLabel="oval-loading"
                    />
                  </div>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const DeletedJobsTable = ({ jobs }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleView = (id) => {
    navigate(`/dashboard/jobs/${id}`);
  };

  // const handleRestore = async (id) => {
  //   try {
  //     setIsRestoring(true);
  //     await dispatch(restoreJob(id)).unwrap();
  //     toast.success('Job restored successfully');
  //     setShowRestoreModal(false);
  //   } catch (error) {
  //     toast.error(error.message || 'Failed to restore job');
  //   } finally {
  //     setIsRestoring(false);
  //   }
  // };
  const handleRestore = async (id) => {
    try {
      setIsRestoring(true);
      await dispatch(restoreJob(id)).unwrap();
      // Fetch the updated list of deleted jobs immediately after restoration
      await dispatch(fetchDeletedJobs({
        page: 1,
        limit: 15,
        activeFilters: {}
      })).unwrap();
      toast.success('Job restored successfully');
      setShowRestoreModal(false);
    } catch (error) {
      toast.error('Failed to restore job');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleForceDelete = async (id) => {
    try {
      setIsDeleting(true);
      await dispatch(forceDeleteJob(id)).unwrap();
      toast.success('Job deleted successfully');
      setShowDeleteModal(false);
    } catch (error) {
      toast.error(error.message || 'Failed to delete job');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white rounded-lg overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Job Details
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Category
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Location
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Salary Range
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Deadline
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {jobs?.map((job) => (
            <tr key={job?.id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    {job?.company_logo ? (
                      <img
                        src={job.company_logo}
                        alt=""
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <Briefcase className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {job?.title}
                    </div>
                    <div className="text-sm text-gray-500">
                      {job?.company_name}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">{job?.job_category?.name}</div>
                <div className="text-sm text-gray-500">{job?.job_type}</div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                <div className="text-sm text-gray-900">{job?.location}</div>
                <div className="text-sm text-gray-500">{job?.location_type}</div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  {`${job?.salary_currency} ${job?.salary_min.toLocaleString()} - ${job?.salary_max.toLocaleString()}`}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  {formatDate(job?.application_deadline)}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => handleView(job.id)}
                    className="text-blue-600 hover:text-blue-900"
                    title="View Details"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedJobId(job.id);
                      setShowDeleteModal(true);
                    }}
                    className="text-red-600 hover:text-red-900"
                    title="Delete Job"
                  >
                    <FaTrashAlt className="h-5 w-5" />
                  </button>

                  <button
                    onClick={() => {
                      setSelectedJobId(job.id);
                      setShowRestoreModal(true);
                    }}
                    className="text-green-600 hover:text-green-900"
                    title="Restore Job"
                  >
                    <Recycle className="h-5 w-5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showRestoreModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full mx-6 lg:max-w-md">
            <h2 className="text-xl font-bold mb-4">Restore Job</h2>
            <p>Are you sure you want to restore this job posting?</p>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowRestoreModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRestore(selectedJobId)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                {isRestoring ? (
                  <div className="flex justify-center items-center">
                    <Oval
                      visible={true}
                      height="20"
                      width="20"
                      color="#ffffff"
                      ariaLabel="oval-loading"
                    />
                  </div>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full mx-6 lg:max-w-md">
            <h2 className="text-xl font-bold mb-4">Permanently Delete Job</h2>
            <p>Are you sure you want to Permanently delete this job posting?</p>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleForceDelete(selectedJobId)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                {isDeleting ? (
                  <div className="flex justify-center items-center">
                    <Oval
                      visible={true}
                      height="20"
                      width="20"
                      color="#ffffff"
                      ariaLabel="oval-loading"
                    />
                  </div>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// export default Jobs;
const Jobs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const jobs = useSelector(state => state.jobs.items);
  const { loading, error, meta, deletedItems } = useSelector((state) => state.jobs);
  const { items: categories, loading: categoriesLoading } = useSelector((state) => state.jobCategories);
  const [activeTab, setActiveTab] = useState('All')

  const {
    pagination,
    handlePageChange,
    updatePaginationData,
    getPageNumbers,
    getItemRange
  } = usePagination({
    initialPage: meta.current_page || 1,
    initialPerPage: meta.per_page || 15
  });

  const [filters, setFilters] = useState({
    type: null,
    category: null,
    location: null
  });

  // Fetch categories on component mount
  useEffect(() => {
    dispatch(fetchJobCategories())
      .unwrap()
      .catch((error) => {
        toast.error('Failed to fetch job categories');
      });
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === 'All') {
      handleFetchJobs();
    } else {
      handleFetchDeletedJobs();
    }
  }, [pagination.currentPage, filters, activeTab, dispatch]);

  const handleFetchJobs = () => {
    const activeFilters = {};
    if (filters.type && filters.type !== 'All Types') {
      activeFilters['filter[job_type]'] = filters.type;
    }
    if (filters.category && filters.category !== 'All Categories') {
      // Find the category ID by name
      const categoryObj = categories.find(cat => cat.name === filters.category);
      if (categoryObj) {
        activeFilters['filter[job_category]'] = categoryObj.name;
      }
    }
    if (filters.location && filters.location !== 'All Locations') {
      activeFilters['filter[location_type]'] = filters.location;
    }

    dispatch(fetchJobs({
      page: pagination.currentPage,
      limit: pagination.perPage,
      activeFilters
    }))
      .unwrap()
      .then(response => {
        if (response.meta) {
          updatePaginationData({
            current_page: response.meta.current_page,
            last_page: response.meta.last_page,
            per_page: response.meta.per_page,
            total: response.meta.total
          });
        }
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to fetch jobs');
      });
  };
  const handleFetchDeletedJobs = () => {
    const activeFilters = {};
    if (filters.type && filters.type !== 'All Types') {
      activeFilters['filter[job_type]'] = filters.type;
    }
    if (filters.category && filters.category !== 'All Categories') {
      // Find the category ID by name
      const categoryObj = categories.find(cat => cat.name === filters.category);
      if (categoryObj) {
        activeFilters['filter[job_category_id]'] = categoryObj.id;
      }
    }
    if (filters.location && filters.location !== 'All Locations') {
      activeFilters['filter[location_types]'] = filters.location;
    }

    dispatch(fetchDeletedJobs({
      page: pagination.currentPage,
      limit: pagination.perPage,
      activeFilters
    }))
      .unwrap()
      .then(response => {
        if (response.meta) {
          updatePaginationData({
            current_page: response.meta.current_page,
            last_page: response.meta.last_page,
            per_page: response.meta.per_page,
            total: response.meta.total
          });
        }
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to fetch jobs');
      });
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value === `All ${filterType}s` ? null : value
    }));
    handlePageChange(1);
  };

  return (
    <div className="p-4">
      <div className="flex flex-col gap-2 sm:flex-row justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Job Postings</h1>
        <JobSearch />
      </div>

      <div className="flex flex-col gap-4">
        <JobFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          categories={categories}
        />

        {/* Main Controls Container - Stack on mobile, row on desktop */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Tab Controls - Horizontal scroll on mobile if needed */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0">
            <button
              onClick={() => setActiveTab('All')}
              className={`whitespace-nowrap font-lufga ${activeTab === 'All'
                  ? 'text-white bg-[#1330C5]'
                  : 'bg-gray-100'
                } hover:opacity-90 focus:outline-none rounded-md shadow-md text-sm sm:text-base px-3 py-2 sm:px-10 sm:py-2`}
            >
              All
            </button>
            <button
              onClick={() => setActiveTab('Deleted')}
              className={`whitespace-nowrap font-lufga ${activeTab === 'Deleted'
                  ? 'text-white bg-[#1330C5]'
                  : 'bg-gray-100'
                } hover:opacity-90 focus:outline-none rounded-md shadow-md text-sm sm:text-base px-3 py-2 sm:px-10 sm:py-2`}
            >
              Deleted Jobs
            </button>
          </div>

          {/* Action Buttons - Stack in grid on mobile, row on desktop */}
          <div className="grid grid-cols-2 gap-2 lg:flex lg:justify-end">
            <button
              onClick={() => navigate('/dashboard/add-job')}
              className="font-lufga text-white bg-[#1330C5] hover:opacity-90 focus:outline-none rounded-md shadow-md text-sm sm:text-base px-3 py-2 sm:px-10 sm:py-2 col-span-2 sm:col-span-1"
            >
              Post New Job
            </button>
            <button
              onClick={() => navigate('/dashboard/job-applications')}
              className="font-lufga bg-gray-100 hover:opacity-90 focus:outline-none rounded-md shadow-md text-sm sm:text-base px-3 py-2 sm:px-10 sm:py-2"
            >
              View Applications
            </button>
            <button
              onClick={() => navigate('/dashboard/job-categories')}
              className="font-lufga bg-gray-100 hover:opacity-90 focus:outline-none rounded-md shadow-md text-sm sm:text-base px-3 py-2 sm:px-10 sm:py-2"
            >
              Job Categories
            </button>
          </div>
        </div>
      </div>

      {(loading || categoriesLoading) ? (
        <div className="flex justify-center items-center h-[80vh]">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
        </div>
      ) : (
        <div className="my-6">
          {activeTab === 'All' ? <JobTable jobs={jobs} /> : <DeletedJobsTable jobs={deletedItems} />}
        </div>
      )}

      {!loading && jobs.length === 0 && activeTab === 'All' && (
        <div className="text-center py-4 text-gray-500">
          No jobs found
        </div>
      )}
      {!loading && deletedItems.length === 0 && activeTab === 'Deleted' && (
        <div className="text-center py-4 text-gray-500">
          No deleted jobs found
        </div>
      )}

      <Pagination
        currentPage={pagination.currentPage}
        lastPage={pagination.lastPage}
        total={pagination.total}
        itemRange={getItemRange()}
        pageNumbers={getPageNumbers()}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default Jobs;